/**
 * WAF cache control (Owl/Mlytics) — per-domain cache rules, tiered-cache toggle
 * and cache purges. Service-scoped (path: `/services/:service_id/`) and domain-anchored
 * (`?domain=<fqdn>`); the provider resolves the customer (tier-2 impersonation)
 * from the service's zone.
 *
 * Backed by `modules/service/waf/controller_cache.go`:
 *   - `GET  /services/:service_id/waf/cache/rules`   → a domain's cache rules (bucket/scope filter, paginated)
 *   - `POST /services/:service_id/waf/cache/rules`   → create a rule (ttl=0 → no-cache/bypass)
 *   - `GET  /services/:service_id/waf/cache/options` → static bucket/scope filter tokens
 *   - `PUT  /services/:service_id/waf/cache/tiered`  → toggle tiered (multi-layer) cache
 *   - `GET  /services/:service_id/waf/cache/purges`  → purge-job history
 *   - `POST /services/:service_id/waf/cache/purges`  → submit a purge for the given paths
 *
 * Note: the backend/SDK currently expose List + Create only for rules — there is
 * no Update or Delete — and no getter for the current tiered-cache state.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  wafKeys,
  unwrap,
  unwrapList,
  type ListResult,
} from './_shared';

/**
 * One cache rule (mirrors BE `providers.WafCacheRule`). `ttl === 0` is a
 * no-cache/bypass rule; `scope` ∈ default | extension | directory | pattern
 * (the `default` rule isn't creatable). `paths` is normalized to a slice.
 */
export interface WafCacheRule {
  id: number;
  scope: string;
  paths: string[];
  ttl: number;
  honor_origin: boolean;
  query_string_mode?: string;
  query_string_value?: string;
}

/** Create body (mirrors BE `createCacheRuleRequest`). `ttl=0` → no-cache rule. */
export interface WafCacheRuleInput {
  scope: string;
  path: string;
  ttl: number;
  honor_origin: boolean;
}

export interface WafCacheRuleFilter {
  /** default | cached | bypass (see useWafCacheOptions). */
  bucket?: string;
  /** extension | directory | pattern. */
  scope?: string;
  page?: number;
  page_size?: number;
}

/** One filter dropdown option (mirrors BE `providers.CacheFilterOption`). */
export interface WafCacheOption {
  value: string;
  label: string;
}

/** Cache-rule filter tokens (mirrors BE `providers.CacheFilterOptionsResponse`). */
export interface WafCacheOptions {
  buckets: WafCacheOption[];
  scopes: WafCacheOption[];
}

/** One cache-purge job (mirrors BE `providers.CachePurge`). Epoch seconds. */
export interface WafCachePurge {
  id: number;
  paths: string[];
  status: string;
  created_at: number;
  updated_at: number;
}

/** List a domain's cache rules (paginated; optional bucket/scope filter). */
export function useWafCacheRules(
  serviceId: string | undefined,
  domain: string | undefined,
  filter: WafCacheRuleFilter = {},
) {
  const params = { domain, ...filter };
  return useQuery<ListResult<WafCacheRule>>({
    queryKey: wafKeys.cacheRules(serviceId ?? '', domain ?? '', filter as Record<string, unknown>),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<WafCacheRule>(await serviceApiV3.get(`/services/${serviceId}/waf/cache/rules`, { params })),
  });
}

/** Cache-rule filter options (bucket/scope dropdown source — static metadata). */
export function useWafCacheOptions(serviceId: string | undefined) {
  return useQuery<WafCacheOptions>({
    queryKey: wafKeys.cacheOptions(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 60 * 60_000,
    queryFn: async () =>
      unwrap<WafCacheOptions>(await serviceApiV3.get(`/services/${serviceId}/waf/cache/options`)),
  });
}

/** Create a cache rule for a domain (`POST /waf/cache/rules`). */
export function useCreateWafCacheRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafCacheRule, Error, { domain: string; input: WafCacheRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: WafCacheRuleInput }) =>
      unwrap<WafCacheRule>(
        await serviceApiV3.post(`/services/${serviceId}/waf/cache/rules`, input, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'cache-rules'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a cache rule (`PUT /waf/cache/rules/:id`) — full replace of scope/path/ttl. */
export function useUpdateWafCacheRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafCacheRule, Error, { domain: string; id: number; input: WafCacheRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, input }: { domain: string; id: number; input: WafCacheRuleInput }) =>
      unwrap<WafCacheRule>(
        await serviceApiV3.put(`/services/${serviceId}/waf/cache/rules/${id}`, input, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'cache-rules'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a cache rule (`DELETE /waf/cache/rules/:id`). */
export function useDeleteWafCacheRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { domain: string; id: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id }: { domain: string; id: number }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/cache/rules/${id}`, {
        params: { domain },
      });
    },
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'cache-rules'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Toggle tiered (multi-layer) caching for a domain (`PUT /waf/cache/tiered`). */
export function useSetTieredCache(
  serviceId: string | undefined,
  options?: UseMutationOptions<{ enable: boolean }, Error, { domain: string; enable: boolean }>,
) {
  return useMutation({
    mutationFn: async ({ domain, enable }: { domain: string; enable: boolean }) =>
      unwrap<{ enable: boolean }>(
        await serviceApiV3.put(`/services/${serviceId}/waf/cache/tiered`, { enable }, {
          params: { domain },
        }),
      ),
    ...options,
  });
}

/** List a domain's cache-purge jobs (newest first, paginated). */
export function useWafCachePurges(
  serviceId: string | undefined,
  domain: string | undefined,
  filter: { page?: number; page_size?: number } = {},
) {
  const params = { domain, ...filter };
  return useQuery<ListResult<WafCachePurge>>({
    queryKey: wafKeys.cachePurges(serviceId ?? '', domain ?? '', filter as Record<string, unknown>),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<WafCachePurge>(await serviceApiV3.get(`/services/${serviceId}/waf/cache/purges`, { params })),
  });
}

/** Submit a cache-purge job for the given paths (`POST /waf/cache/purges`). */
export function useCreateWafCachePurge(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafCachePurge, Error, { domain: string; paths: string[] }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, paths }: { domain: string; paths: string[] }) =>
      unwrap<WafCachePurge>(
        await serviceApiV3.post(`/services/${serviceId}/waf/cache/purges`, { paths }, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'cache-purges'] });
      options?.onSuccess?.(...args);
    },
  });
}
